import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useThemeContext } from '../../../contexts/ThemeContext';

export default function HeroSlider() {
  const { theme } = useThemeContext();
  const slides = theme.slider;
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (transitioning || slides.length === 0) return;
    setTransitioning(true);
    setTimeout(() => { setCurrent(index); setTransitioning(false); }, 300);
  }, [transitioning, slides.length]);

  const next = useCallback(() => {
    if (slides.length === 0) return;
    goTo((current + 1) % slides.length);
  }, [current, goTo, slides.length]);

  useEffect(() => {
    if (slides.length === 0) return;
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next, slides.length]);

  useEffect(() => { setCurrent(0); }, [slides.length]);

  if (slides.length === 0) return null;

  const slide = slides[current];

  return (
    <section className="relative w-full h-screen min-h-[680px] max-h-[960px] overflow-hidden">
      {slides.map((s, i) => (
        <div key={s.id} className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
          <img src={s.image_url} alt={s.title} className="w-full h-full object-cover object-top" />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

      <div className="relative z-10 h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            {slide.badge && (
              <div className={`inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full mb-6 transition-all duration-500 ${transitioning ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'}`}>
                <div className="w-4 h-4 flex items-center justify-center bg-gold rounded-full">
                  <i className="ri-star-fill text-white text-xs" />
                </div>
                <span className="text-white text-xs font-medium tracking-wide">{slide.badge}</span>
              </div>
            )}
            <h1 className={`font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6 whitespace-pre-line transition-all duration-500 delay-100 ${transitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {slide.title}
            </h1>
            <p className={`text-base lg:text-lg text-white/80 leading-relaxed mb-8 max-w-lg transition-all duration-500 delay-150 ${transitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {slide.description}
            </p>
            <div className={`flex flex-wrap items-center gap-4 transition-all duration-500 delay-200 ${transitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <Link to={slide.cta_link} className="px-8 py-3.5 bg-gold hover:bg-gold-dark text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap text-sm">
                {slide.cta_text}
              </Link>
              <Link to="/iletisim" className="px-8 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium rounded-xl transition-all duration-300 whitespace-nowrap text-sm backdrop-blur-sm">
                Hikayemiz
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => goTo(i)} className={`transition-all duration-300 rounded-full cursor-pointer ${i === current ? 'w-8 h-2 bg-gold' : 'w-2 h-2 bg-white/40 hover:bg-white/70'}`} aria-label={`Slayt ${i + 1}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => goTo((current - 1 + slides.length) % slides.length)} className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white transition-colors backdrop-blur-sm cursor-pointer" aria-label="Önceki">
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button onClick={next} className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white transition-colors backdrop-blur-sm cursor-pointer" aria-label="Sonraki">
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
